import cx from 'classnames'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { isEmpty } from 'lodash'

import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import {
  PLACEHOLDER_GROUP_COVER_IMAGE,
  PLACEHOLDER_USER_COVER_IMAGE,
} from 'lib/image'
import { GameApp, Gender, Group, GroupCreate } from 'lib/_types'
import { useGetGroupCategoriesQuery } from 'store/api/yay/group'
import { useCurrentUser } from 'store/api/yay/user'
import { Link } from 'features/i18n/router'
import { useBanWord, useUploadImage, useVipFeatureRestriction } from 'hooks'
import { useGetGameAppQuery } from 'store/api/yay/games'
import {
  GAME_CATEGORY_ID,
  MAX_GROUP_TOPIC_LENGTH,
  MAX_GROUP_DESCRIPTION_LENGTH,
  MAX_GROUP_GUIDELINES_LENGTH,
} from 'config'

import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import ImageLoader from 'components/ImageLoader'
import { Switch } from 'components/Form/Switch'
import { SelectGameTitleModal } from './SelectGameTitleModal'

const GroupSettingsForm = (props: {
  group?: Group
  onSubmit: (formData: GroupCreate) => Promise<void>
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isContainBanWord, showDetectedBanWordAlert } = useBanWord()
  const { showConfirmBox } = useGlobalConfirmBox()
  const { upload } = useUploadImage()

  const [__isLoading, __isLoadingSet] = useState(false)

  const [__topic, __topicSet] = useState(props?.group?.topic ?? '')

  const [__isActiveConfirmBoxGender, __isActiveConfirmBoxGenderSet] =
    useState(false)

  const [__isActiveAdditionalSettings, __isActiveAdditionalSettingsSet] =
    useState(false)

  const [__description, __descriptionSet] = useState(
    props.group?.description || ''
  )

  const [__guidelines, __guidelinesSet] = useState(
    props.group?.guidelines || ''
  )

  const [__groupCategoryId, __groupCategoryIdSet] = useState<number | string>(
    props.group?.group_category_id || ''
  )

  const [__gameAppId, __gameAppIdSet] = useState<number>(
    props.group?.sub_category_id
  )

  const [__groupCoverImage, __groupCoverImageSet] = useState<File>(null)
  const [__groupIconImage, __groupIconImageeSet] = useState<File>(null)

  const [__isGameTitleModalActive, __isGameTitleModalActiveSet] =
    useState(false)

  const [__isCategoryModalActive, __isCategoryModalActiveSet] = useState(false)

  const { data: currentUser } = useCurrentUser()

  const { isCurrentUserVip, showVipFeatureRestrictionBox } =
    useVipFeatureRestriction()

  const groupsCategoriesRes = useGetGroupCategoriesQuery({})

  const gameAppRes = useGetGameAppQuery(
    { id: __gameAppId },
    {
      skip: !__gameAppId,
    }
  )

  const isEmptyGroupCategoryId =
    __groupCategoryId === '' || __groupCategoryId === 0

  const isSubmitButtonFakeDisabled = isEmptyGroupCategoryId || __topic === ''

  const isEditGroup = props.group

  const defaultValues = useMemo(() => {
    return {
      cover_image: props.group?.cover_image,
      group_icon: props.group?.group_icon,
      sub_category_id: props.group?.sub_category_id,
      is_private: props.group?.is_private ?? false,
      call_timeline_display: props.group?.call_timeline_display ?? true,
      hide_reported_posts: props.group?.hide_reported_posts ?? false,
      allow_ownership_transfer: props.group?.allow_ownership_transfer ?? true,
      allow_thread_creation_by:
        props.group?.allow_thread_creation_by ?? 'member',
      allow_members_to_post_image_and_video:
        props.group?.allow_members_to_post_image_and_video ?? true,
      allow_members_to_post_url: props.group?.allow_members_to_post_url ?? true,
      hide_conference_call: props.group?.hide_conference_call ?? false,
      hide_from_game_eight: props.group?.hide_from_game_eight ?? false,
      secret: props.group?.secret ?? false,
      only_verified_age: props.group?.only_verified_age ?? false,
      only_mobile_verified: props.group?.only_mobile_verified ?? false,
      gender: props.group?.gender ?? Gender.UNSET,
      generation_groups_limit: props.group?.generation_groups_limit ?? '0',
    }
  }, [props.group])

  const groupCoverImage = useMemo(
    () =>
      __groupCoverImage
        ? `url(${URL.createObjectURL(__groupCoverImage)})`
        : defaultValues.cover_image
        ? `url(${defaultValues.cover_image})`
        : `url(${PLACEHOLDER_GROUP_COVER_IMAGE})`,
    [defaultValues, __groupCoverImage]
  )

  const groupIconImage = useMemo(
    () =>
      __groupIconImage
        ? `url(${URL.createObjectURL(__groupIconImage)})`
        : defaultValues.group_icon
        ? `url(${defaultValues.group_icon})`
        : `url(${PLACEHOLDER_USER_COVER_IMAGE})`,
    [defaultValues, __groupIconImage]
  )

  const _onSubmit = async (e: any) => {
    e.preventDefault()
    __isLoadingSet(true)

    if (
      isContainBanWord(__topic, 'group') ||
      isContainBanWord(__description, 'group') ||
      isContainBanWord(__guidelines, 'group')
    ) {
      showDetectedBanWordAlert()
      __isLoadingSet(false)
      return
    }

    if (isEmpty(__topic) || isEmptyGroupCategoryId) {
      let _title = ''
      let _content = ''

      if (isEmpty(__topic) && isEmptyGroupCategoryId) {
        _title = t(
          'group_settings_modal.alert_group_create_enter_name_and_category_title'
        )

        _content = t(
          'group_settings_modal.alert_group_create_enter_name_and_category_message'
        )
      } else if (isEmpty(__topic)) {
        _title = t('group_settings_modal.alert_group_create_enter_name_title')

        _content = t(
          'group_settings_modal.alert_group_create_enter_name_message'
        )
      } else {
        _title = t(
          'group_settings_modal.alert_group_create_enter_category_title'
        )

        _content = t(
          'group_settings_modal.alert_group_create_enter_category_message'
        )
      }

      showConfirmBox({
        title: _title,
        content: _content,
        confirmBoxProps: {
          cancelContent: <></>,
        },
      })

      __isLoadingSet(false)

      return
    }

    const getElement = (name: string) =>
      (e.target as HTMLFormElement).elements.namedItem(name) as HTMLInputElement

    const data: GroupCreate = {
      topic: __topic,
      description: __description,
      guidelines: __guidelines,
      group_category_id: +__groupCategoryId,
      sub_category_id:
        __groupCategoryId === GAME_CATEGORY_ID ? +__gameAppId : 0,
      call_timeline_display: !getElement('call_timeline_display')?.checked,
      allow_thread_creation_by: getElement('allow_thread_creation_by')?.checked
        ? 'member'
        : 'moderator',
      // https://nanameue.slack.com/archives/G01L4EU4DQA/p1652409918842869?thread_ts=1652408440.780279&cid=G01L4EU4DQA
      gender: getElement('gender')?.checked ? currentUser?.gender : -1,
      // Documentations:
      // https://wiki.nanameue.jp/display/YAYAPP/Yay+1.3#Yay1.3-YAY-120Addcirclesettingthatuseragerange:morethan18,lessthan18orall.
      // https://wiki.nanameue.jp/display/YAYAPP/Generation+group
      generation_groups_limit: getElement('generation_groups_limit')?.checked
        ? currentUser?.generation >= 2
          ? 2
          : 1
        : 0,
    }

    ;[
      'is_private',
      'hide_reported_posts',
      'allow_ownership_transfer',
      'allow_members_to_post_image_and_video',
      'allow_members_to_post_url',
      'hide_conference_call',
      'hide_from_game_eight',
      'secret',
      'only_verified_age',
      'only_mobile_verified',
    ].forEach((name) => {
      data[name] = getElement(name)?.checked
    })

    if (__groupCoverImage) {
      try {
        const uploadRes = await upload([__groupCoverImage], 'group_cover')
        data.cover_image_filename = uploadRes.find((x) => !x.isThumb)?.fileName
      } catch {
        __isLoadingSet(false)
        return
      }
    }

    if (__groupIconImage) {
      try {
        const uploadRes = await upload([__groupIconImage], 'group_icon')
        data.group_icon_filename = uploadRes.find((x) => !x.isThumb)?.fileName
      } catch {
        __isLoadingSet(false)
        return
      }
    }

    await props.onSubmit(data)

    __isLoadingSet(false)
  }

  return (
    <>
      {!__isCategoryModalActive && (
        <form className="Modal__wrapper" onSubmit={_onSubmit}>
          <div className="Modal__header">
            <Link className="Modal__header__close" to={location?.pathname} />

            <h2 className="Modal__header__h2">
              {isEditGroup
                ? t('group_settings_modal.group_info_settings')
                : t('create_group_modal.title')}
            </h2>
          </div>

          <div
            className={cx('Modal__body', {
              'Modal__body--loading': __isLoading,
            })}
          >
            <div className="GroupNew">
              <div
                className="GroupNew__header"
                style={{ backgroundImage: groupCoverImage }}
              >
                <div className="GroupNew__header__wrapper"></div>

                <div className="GroupNew__header__upload-cover">
                  <label className="GroupNew__header__upload-cover__handle">
                    <input
                      type="file"
                      accept=".jpg, .jpeg, .png, .gif"
                      onChange={(e) => {
                        __groupCoverImageSet(e.target.files[0])
                      }}
                    />
                  </label>
                </div>

                <div className="GroupNew__header__upload-icon">
                  <label
                    className="GroupNew__header__upload-icon__handle"
                    style={{
                      backgroundImage: groupIconImage,
                    }}
                  >
                    <input
                      type="file"
                      accept=".jpg, .jpeg, .png, .gif"
                      onChange={(e) => {
                        __groupIconImageeSet(e.target.files[0])
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="GroupNew__body">
                <label className="GroupNew__body__label GroupNew__body__label--full">
                  <span className="GroupNew__body__label__span GroupNew__body__label__span--bold">
                    {t('create_group_modal.topic.label')}
                  </span>

                  <textarea
                    className="Input"
                    rows={3}
                    value={__topic}
                    onKeyDown={(e) => {
                      if (e?.key?.toLowerCase?.() == 'enter') {
                        e.preventDefault()
                      }
                    }}
                    onChange={(e) => {
                      __topicSet(
                        e?.target?.value?.substring?.(0, MAX_GROUP_TOPIC_LENGTH)
                      )
                    }}
                  />

                  <p className="GroupNew__body__description__hint">
                    {__topic.length}/{MAX_GROUP_TOPIC_LENGTH}
                  </p>
                </label>

                <label className="GroupNew__body__label GroupNew__body__label--full">
                  <span className="GroupNew__body__label__span GroupNew__body__label__span--bold">
                    {t('create_group_modal.description.label')}
                  </span>

                  <textarea
                    className="Input"
                    rows={3}
                    value={__description}
                    onChange={(e) => {
                      __descriptionSet(
                        e.target.value.slice(0, MAX_GROUP_DESCRIPTION_LENGTH)
                      )
                    }}
                  />

                  <p className="GroupNew__body__description__hint">
                    {__description.length}/{MAX_GROUP_DESCRIPTION_LENGTH}
                  </p>
                </label>

                <label className="GroupNew__body__label GroupNew__body__label--full">
                  <span className="GroupNew__body__label__span GroupNew__body__label__span--bold">
                    {t('create_group_modal.guidelines.label')}
                  </span>

                  <textarea
                    name="guidelines"
                    className="Input"
                    rows={3}
                    value={__guidelines}
                    onChange={(e) => {
                      __guidelinesSet(
                        e.target.value.slice(0, MAX_GROUP_GUIDELINES_LENGTH)
                      )
                    }}
                  />

                  <p className="GroupNew__body__description__hint">
                    {__guidelines.length}/{MAX_GROUP_GUIDELINES_LENGTH}
                  </p>
                </label>

                <a
                  className="GroupNew__body__label"
                  onClick={() => __isCategoryModalActiveSet(true)}
                >
                  <span className="GroupNew__body__label__span">
                    {t('create_group_modal.group_category.label')}
                  </span>

                  <div className="GroupNew__body__label__right">
                    <div className="GroupNew__body__label__right__group-category">
                      {isEmptyGroupCategoryId ? (
                        <span className="GroupNew__body__label__right__group-category__select-required">
                          {t(
                            'create_group_modal.group_category_selection_required'
                          )}
                        </span>
                      ) : (
                        <>
                          {(() => {
                            const selectedGroupCategory =
                              groupsCategoriesRes?.data?.find?.(
                                (x) => x?.id == __groupCategoryId
                              )

                            return (
                              <div className="GroupNew__body__label__right__group-category__wrapper">
                                <ImageLoader
                                  url={selectedGroupCategory?.icon}
                                  className="GroupNew__body__label__right__group-category__img"
                                />

                                <span className="GroupNew__body__label__right__group-category__span">
                                  {selectedGroupCategory?.name}
                                </span>
                              </div>
                            )
                          })()}
                        </>
                      )}
                    </div>

                    <span
                      className={cx('GroupNew__body__label__caret', {
                        'GroupNew__body__label__caret--right': true,
                      })}
                    />
                  </div>
                </a>

                {__groupCategoryId === GAME_CATEGORY_ID && (
                  <a
                    className="GroupNew__body__label"
                    onClick={() => {
                      __isGameTitleModalActiveSet(true)
                    }}
                  >
                    <span className="GroupNew__body__label__span">
                      {t('common.game_title')}
                    </span>

                    <div className="GroupNew__body__label__right">
                      {__gameAppId ? (
                        <>
                          <div className="GroupNew__body__label__right__group-category__wrapper">
                            <ImageLoader
                              url={gameAppRes?.data?.icon_url}
                              className="GroupNew__body__label__right__group-category__img"
                            />

                            <span className="GroupNew__body__label__right__group-category__span">
                              {gameAppRes?.data?.title}
                            </span>
                          </div>

                          <a
                            className="GroupNew__body__label__right__deselect"
                            onClick={(e) => {
                              e.stopPropagation()
                              __gameAppIdSet(null)
                            }}
                          >
                            <img src="/images/icon-x-o-gray.svg" />
                          </a>
                        </>
                      ) : (
                        <>
                          <div className="GroupNew__body__label__right__group-category">
                            <span className="GroupNew__body__label__right__group-category__select-required">
                              {t('common.not_set')}
                            </span>
                          </div>

                          <span
                            className={cx('GroupNew__body__label__caret', {
                              'GroupNew__body__label__caret--right': true,
                            })}
                          />
                        </>
                      )}
                    </div>
                  </a>
                )}

                <a
                  className={cx('GroupNew__body__label', {
                    'GroupNew__body__label--last':
                      !__isActiveAdditionalSettings,
                  })}
                  onClick={() => {
                    __isActiveAdditionalSettingsSet(
                      !__isActiveAdditionalSettings
                    )
                  }}
                >
                  <span className="GroupNew__body__label__span">
                    {t('group_settings_modal.group_additional_settings')}
                  </span>

                  <span
                    className={cx('GroupNew__body__label__caret', {
                      'GroupNew__body__label__caret--active':
                        __isActiveAdditionalSettings,
                    })}
                  />
                </a>

                <div
                  className={cx('GroupNew__body__additional-settings', {
                    'GroupNew__body__additional-settings--active':
                      __isActiveAdditionalSettings,
                  })}
                >
                  {[
                    {
                      h4: t(
                        'group_settings_modal.group_settings_section_general'
                      ),
                      items: [
                        {
                          dt: t(
                            'group_settings_modal.group_info_membership_approval'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_private_description'
                          ),
                          name: 'is_private',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_hide_calls_title'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_hide_calls_description'
                          ),
                          name: 'call_timeline_display',
                          isChecked:
                            defaultValues.call_timeline_display == false,
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_hide_inappropriate_posts'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_hide_inappropriate_posts_description'
                          ),
                          name: 'hide_reported_posts',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_group_owner_transfer_title'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_group_owner_transfer_description'
                          ),
                          name: 'allow_ownership_transfer',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_create_thread'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_create_thread_description'
                          ),
                          name: 'allow_thread_creation_by',
                          isChecked:
                            defaultValues.allow_thread_creation_by == 'member',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_create_image_video_post'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_create_image_video_post_description'
                          ),
                          name: 'allow_members_to_post_image_and_video',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_create_url_post'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_create_url_description'
                          ),
                          name: 'allow_members_to_post_url',
                        },
                      ],
                    },
                    {
                      h4: t(
                        'group_settings_modal.group_settings_section_visibility'
                      ),
                      items: [
                        {
                          dt: t(
                            'group_settings_modal.group_settings_hide_calls_at_group_category_list_title'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_hide_calls_at_group_category_list_description'
                          ),
                          name: 'hide_conference_call',
                        },
                        ...(props?.group?.group_category_id == 4
                          ? [
                              {
                                dt: t(
                                  'group_settings_modal.group_settings_hide_group_from_game_eight_title'
                                ),
                                dd: t(
                                  'group_settings_modal.group_settings_hide_group_from_game_eight_description'
                                ),
                                name: 'hide_from_game_eight',
                              },
                            ]
                          : []),
                        {
                          dt: t(
                            'group_settings_modal.group_settings_invite_only_title'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_invite_only_description'
                          ),
                          name: 'secret',
                          onlyVip: true,
                        },
                      ],
                    },
                    {
                      h4: t(
                        'group_settings_modal.group_settings_section_restrictions'
                      ),
                      items: [
                        {
                          dt: t(
                            'group_settings_modal.group_settings_age_verified_only_title'
                          ),
                          name: 'only_verified_age',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_mobile_verified_only_title'
                          ),
                          name: 'only_mobile_verified',
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_same_gender_only_title_unset'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_same_gender_only_description'
                          ),
                          name: 'gender',
                          isChecked: defaultValues.gender != -1,
                          onClick:
                            currentUser?.gender == -1
                              ? () => {
                                  __isActiveConfirmBoxGenderSet(true)
                                }
                              : null,
                        },
                        {
                          dt: t(
                            'group_settings_modal.group_settings_same_generation_only_title_unset'
                          ),
                          dd: t(
                            'group_settings_modal.group_settings_same_generation_only_description'
                          ),
                          name: 'generation_groups_limit',
                          isChecked: defaultValues.generation_groups_limit != 0,
                        },
                      ],
                    },
                  ]?.map?.((section, i) => (
                    <div
                      className="GroupNew__body__additional-settings__section"
                      key={i}
                    >
                      <h4 className="GroupNew__body__additional-settings__section__h4">
                        {section?.h4}
                      </h4>

                      <dl className="GroupNew__body__additional-settings__section__dl">
                        {section?.items?.map?.(
                          (
                            item: {
                              dt: string
                              dd?: string
                              name: string
                              onlyVip?: boolean
                              isChecked?: boolean
                              onClick?: Function | null
                            },
                            i: number
                          ) => {
                            let onClick = item?.onClick

                            if (item?.onlyVip && !isCurrentUserVip) {
                              onClick = showVipFeatureRestrictionBox
                            }

                            return (
                              <div
                                className="GroupNew__body__additional-settings__section__dl__item"
                                key={i}
                              >
                                <div className="GroupNew__body__additional-settings__section__dl__item__wrapper">
                                  <dt className="GroupNew__body__additional-settings__section__dl__item__dt">
                                    {item?.dt}
                                  </dt>

                                  <SwitchWrapper
                                    checked={
                                      item?.isChecked ??
                                      defaultValues?.[item?.name] == true
                                    }
                                    name={item?.name}
                                    onClick={onClick}
                                  />
                                </div>

                                {item?.dd && (
                                  <dd className="GroupNew__body__additional-settings__section__dl__item__dd">
                                    {item?.dd}
                                  </dd>
                                )}
                              </div>
                            )
                          }
                        )}
                      </dl>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="Modal__footer">
            <button
              className={cx('Button', {
                'Button--fake-disabled': isSubmitButtonFakeDisabled,
              })}
              disabled={__isLoading}
            >
              {isEditGroup
                ? t('group_settings_modal.group_edit_action')
                : t('create_group_modal.create_group_button')}
            </button>
          </div>

          <ConfirmBoxBasic
            isActive={__isActiveConfirmBoxGender}
            onClose={() => {
              __isActiveConfirmBoxGenderSet(false)
            }}
            onOk={() => {
              __isActiveConfirmBoxGenderSet(false)
            }}
            title={t(
              'group_settings_modal.group_settings_age_verified_only_title'
            )}
            content={t(
              'group_settings_modal.group_settings_age_verified_only_title_modal'
            )}
          />
        </form>
      )}

      <SelectGameTitleModal
        isActive={__isGameTitleModalActive}
        onClose={() => __isGameTitleModalActiveSet(false)}
        onSelect={(game: GameApp) => {
          if (
            props?.group?.sub_category_id &&
            props?.group?.sub_category_id !== game?.id &&
            game?.id !== __gameAppId
          ) {
            showConfirmBox({
              title: t('group_settings_modal.change_game_app_title', {
                gameTitle: game?.title,
              }),
              content: t('group_settings_modal.change_game_app_description'),
              confirmBoxProps: {
                onOk: () => {
                  __gameAppIdSet(game.id)
                  __isGameTitleModalActiveSet(false)
                },
              },
            })
          } else {
            __gameAppIdSet(game.id)
            __isGameTitleModalActiveSet(false)
          }
        }}
      />

      {__isCategoryModalActive && (
        <div
          className={cx('Modal__wrapper', {
            'Modal__wrapper--auto-h': true,
            'Modal__wrapper--auto-w': true,
            GroupCategoryModal: true,
          })}
        >
          <div className="Modal__header">
            <a
              className="Modal__header__close Modal__header__close--back"
              onClick={() => {
                __isCategoryModalActiveSet(false)
              }}
            />

            <h2 className="Modal__header__h2">
              {t('common.circle_categories')}
            </h2>
          </div>

          <div className="Modal__body">
            <div className="GroupCategoryModal__wrapper">
              <dl className="GroupCategoryModal__dl">
                {groupsCategoriesRes?.data?.map?.((x, i) => (
                  <a
                    className="GroupCategoryModal__dl__item"
                    key={i}
                    onClick={() => {
                      __groupCategoryIdSet(x?.id)
                      __isCategoryModalActiveSet(false)
                    }}
                  >
                    <div className="GroupCategoryModal__dl__item__image">
                      <ImageLoader
                        url={x?.icon}
                        className="GroupCategoryModal__dl__item__image__img"
                      />
                    </div>

                    <dt className="GroupCategoryModal__dl__item__dt">
                      {x?.name}
                    </dt>
                  </a>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GroupSettingsForm

const SwitchWrapper = (props: {
  checked: boolean
  name: string
  onClick?: Function | null
}) => {
  const [__isChecked, __isCheckedSet] = useState(props.checked)

  return (
    <Switch
      onChange={(checked: boolean) => {
        if (props.onClick) {
          props.onClick?.()
          return
        }

        __isCheckedSet(checked)
      }}
      small={false}
      disabled={false}
      checked={__isChecked}
      name={props.name}
    />
  )
}
